import { queryOptions, useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentRecord } from "../AgentRecord";
import { type AgentService } from "../AgentService";

interface UseAgentOptions {
  /** Caller's org id; the backend requires it on every read. */
  orgId?: string;
}

/** Where one agent sits in the query cache. */
export function agentQueryKey(agentId: string | undefined) {
  return ["agent", agentId] as const;
}

/**
 * The query that reads one agent, shared by {@link useAgent} and the agent
 * routes' loader so a primed cache entry is the one the page then reads.
 *
 * Disabled until both ``agentId`` and ``orgId`` are defined so callers can
 * mount before route params resolve without firing a doomed request.
 */
export function agentQueryOptions(
  agentId: string | undefined,
  orgId: string | undefined,
  agentService: AgentService,
) {
  return queryOptions<AgentRecord, Error>({
    queryKey: agentQueryKey(agentId),
    queryFn: ({ signal }) => {
      if (agentId === undefined) {
        return Promise.reject(new Error("agentId is required"));
      }
      return agentService.getAgent(agentId, { orgId, abortSignal: signal });
    },
    enabled: agentId !== undefined && orgId !== undefined,
    staleTime: 30_000,
  });
}

/** Fetch a single Agent by id. */
export function useAgent(
  agentId: string | undefined,
  options?: UseAgentOptions,
): UseQueryResult<AgentRecord, Error> {
  const { agentService } = useDomainServices();

  return useQuery(agentQueryOptions(agentId, options?.orgId, agentService));
}
